import { getConstants, getFAQPage } from '@lib/sanity';
import Head from 'next/head';

import type { Constants, FAQ } from '@lib/queries';
import type { GetServerSideProps, Metadata } from 'next';

import { Accordion } from '~/components/accordion';
import { Box } from '~/components/box';
import { setAppContext } from '~/components/context';
import { CtaBlock } from '~/components/cta-block';
import { PageWrapper } from '~/components/wrapper';

type Props = {
  data?: FAQ;
  context: Constants;
};

export const metadata: Metadata = {
  description:
    'We have curated a list of FAQs to help answer any burning questions you may have. If there is a missing piece you are craving, do not hesitate to hit us up at contact@fyr.com.au',
  robots: {
    follow: true,
    googleBot: {
      index: true,
      follow: false,
      noimageindex: true,
      'max-image-preview': 'large',
      'max-snippet': -1,
      'max-video-preview': -1,
    },
    index: false,
    nocache: true,
  },
  title: 'FAQ | Find Your Rhythm',
};

const FAQPage = ({ data, context }: Props) => {
  if (!data) return;

  setAppContext(context);

  return (
    <PageWrapper>
      <Head>
        <title>FAQ | Find Your Rhythm</title>
        <meta
          content="We have curated a list of FAQs to help answer any burning questions you may have. If there is a missing piece you are craving, do not hesitate to hit us up at contact@fyr.com.au"
          name="description"
          property="og:description"
        />
        <meta
          content="index, max-image-preview=large, max-snippet=-1, max-video-preview=-1, nofollow, noimageindex"
          name="googlebot"
        />
        <meta content="follow, nocache, noindex" name="robots" />
        <meta content="FAQ | Find Your Rhythm" key="title" name="title" property="og:title" />
      </Head>
      <Box>
        <Box width="full" marginTop={{ lg: '5' }}>
          <Accordion {...data.faq} />
        </Box>
      </Box>
      {data.ctaBlock && (
        <Box>
          <CtaBlock {...data.ctaBlock} />
        </Box>
      )}
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  const faqPage = await getFAQPage();
  const context = await getConstants();

  return {
    props: {
      data: faqPage,
      context,
    },
  };
};

export default FAQPage;
